import { MEMBERSTATE, USERSSTATE } from '.'
import type { MemberRoom, MembersRecent, MemberWebcam } from '../../types'
import type {
    MembersFeedError,
    MembersState,
    MemberTagsState,
} from '../../types/state'
import { findMemberRoomByOwner } from '../mfcLogic/memberRooms'
import { slugToID } from './internal/users'
import { pubSubMembers } from './pubSub'

const setState = (updates: Partial<MembersState>) => {
    MEMBERSTATE.members = { ...MEMBERSTATE.members, ...updates }
    pubSubMembers.emit('state', MEMBERSTATE.members)
}

const setTagsState = (updates: Partial<MemberTagsState>) => {
    setState({ tags: { ...MEMBERSTATE.members.tags, ...updates } })
}

const owner = { override: null as number | null }

const memberRoomJoin = { ownerId: null as number | null }

export const markMemberRoomJoin = (ownerId: number) => {
    memberRoomJoin.ownerId = ownerId
}

export const takeMemberRoomJoin = (ownerId: number) => {
    const requested = memberRoomJoin.ownerId === ownerId
    memberRoomJoin.ownerId = null
    return requested
}

const ownUserId = () =>
    owner.override ?? USERSSTATE.users[slugToID['me']]?.id ?? null

const findMyRoomId = (rooms: Record<number, MemberRoom>) => {
    const id = ownUserId()
    return (id && findMemberRoomByOwner(rooms, id)?.id) || null
}

export const setMembersOwnUserId = (id: number | null) => {
    owner.override = id
    setState({ myRoomId: findMyRoomId(MEMBERSTATE.members.rooms) })
}

export const setMemberWebcams = (webcams: Array<MemberWebcam>) => {
    setState({ webcams, loading: false, webcamsError: null })
}

export const setMemberWebcamsError = (
    webcamsError: MembersFeedError | null,
) => {
    setState({ webcamsError, loading: false })
}

export const setMyMemberWebcam = (myWebcam: MemberWebcam | null) => {
    setState({ myWebcam })
}

// mirrors the www option; the page filters genders with its own chips
export const setMemberWebcamSelectionMask = (webcamSelectionMask: number) => {
    if (webcamSelectionMask === MEMBERSTATE.members.webcamSelectionMask) return
    setState({ webcamSelectionMask })
}

export const setMemberRooms = (rooms: Record<number, MemberRoom>) => {
    setState({
        rooms,
        myRoomId: findMyRoomId(rooms),
        roomsLoaded: true,
        roomsFresh: true,
        roomsError: null,
    })
}

export const setMemberRoomsError = (roomsError: MembersFeedError | null) => {
    setState({ roomsError })
}

export const resetMemberRooms = () => {
    setState({
        rooms: {},
        myRoomId: null,
        roomsLoaded: false,
        roomsFresh: false,
    })
}

export const markMemberRoomsStale = () => {
    if (!MEMBERSTATE.members.roomsFresh) return
    setState({ roomsFresh: false })
}

export const clearMemberWebcams = () => {
    if (!MEMBERSTATE.members.webcams.length) return
    setState({ webcams: [] })
}

export const setMemberCreateRoomError = (
    createRoomError: MembersFeedError | null,
) => {
    setState({ createRoomError })
}

export const setMemberRoom = (room: MemberRoom) => {
    const rooms = { ...MEMBERSTATE.members.rooms, [room.id]: room }
    setState({ rooms, myRoomId: findMyRoomId(rooms) })
}

export const removeMemberRoom = (id: number) => {
    const { [id]: removed, ...rooms } = MEMBERSTATE.members.rooms
    if (!removed) return
    setState({ rooms, myRoomId: findMyRoomId(rooms) })
}

export const setMembersLoading = (loading: boolean) => {
    setState({ loading })
}

export const setMembersRecent = (recent: MembersRecent) => {
    setState({ recent })
}

export const setMembersComposing = (composing: boolean) => {
    setState({ composing })
}

export const setMemberTagsLoading = (loading: boolean) => {
    setTagsState({
        loading,
        ...(loading ? { error: null, listLoaded: false } : {}),
    })
}

export const setMemberTagsError = (error: MembersFeedError | null) => {
    setTagsState({ error, loading: false })
}

export const setMemberTagList = (list: MemberTagsState['list']) => {
    setTagsState({ list, listLoaded: true, loading: false, error: null })
}

export const setMemberTagSelected = (selected: string) => {
    setTagsState({ selected, members: [] })
}

export const setMemberTaggedLoading = () => {
    setTagsState({ membersLoading: true, membersError: null })
}

export const setMemberTaggedMembers = (members: MemberTagsState['members']) => {
    setTagsState({ members, membersLoading: false, membersError: null })
}

export const setMemberTaggedError = (membersError: MembersFeedError) => {
    setTagsState({ membersError, membersLoading: false })
}

export const setMyMemberTagsLoading = () => {
    setTagsState({ mineLoaded: false, mineError: null })
}

export const setMyMemberTags = (mine: Array<string>) => {
    setTagsState({ mine, mineLoaded: true, mineError: null })
}

export const setMyMemberTagsError = (mineError: MembersFeedError) => {
    setTagsState({ mineError })
}

export const resetMembersState = (state: MembersState) => {
    MEMBERSTATE.members = state
    pubSubMembers.emit('state', MEMBERSTATE.members)
}
